import { useCallback, useEffect, useMemo, useState } from 'react';
import { listSubmissions } from '../lib/api';
import type { ModerationHistoryEntry } from '../types/moderation';
import Select from './ui/Select';
import { formatDateTime } from '../lib/format';
import { useCurrentUser } from '../lib/auth';
import ErrorState from './ErrorState';
import { buildCsv, downloadCsv } from '../lib/csv';
import { SkeletonRow } from './ui/Skeleton';
import EmptyState from './ui/EmptyState';

interface ActivityLogProps {
  onSelect: (submissionId: string) => void;
}

interface ActivityEntry extends ModerationHistoryEntry {
  submissionId: string;
  submissionTitle: string;
}

const ACTION_OPTIONS = [
  'Any action',
  'approved',
  'rejected',
  'withdrawn',
  'pending_review',
];

export default function ActivityLog({ onSelect }: ActivityLogProps) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('Any action');
  const [userFilter, setUserFilter] = useState('');
  const [moderatorFilter, setModeratorFilter] = useState('All moderators');
  const [reloadKey, setReloadKey] = useState(0);
  const currentUser = useCurrentUser();
  const role = currentUser?.role ?? 'volunteer';
  const isLeadOrAdmin = role === 'lead_moderator' || role === 'admin';

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSubmissions({ pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const flat: ActivityEntry[] = [];
        for (const item of res.items) {
          for (const entry of item.moderationHistory || []) {
            flat.push({
              ...entry,
              submissionId: item.submissionId,
              submissionTitle: item.title,
            });
          }
        }
        flat.sort((a, b) => {
          const ta = new Date(a.timestamp).getTime();
          const tb = new Date(b.timestamp).getTime();
          return tb - ta;
        });
        setEntries(flat.slice(0, 50));
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load activity');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const moderatorOptions = useMemo(() => {
    const emails = Array.from(
      new Set(entries.map((e) => e.userEmail).filter(Boolean)),
    ).sort();
    return ['All moderators', ...emails];
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (actionFilter !== 'Any action' && e.action !== actionFilter) return false;
      if (userFilter && !e.userEmail.toLowerCase().includes(userFilter.toLowerCase()))
        return false;
      if (moderatorFilter !== 'All moderators' && e.userEmail !== moderatorFilter)
        return false;
      return true;
    });
  }, [entries, actionFilter, userFilter, moderatorFilter]);

  const exportCsv = () => {
    const csv = buildCsv(
      [
        { key: 'timestamp', label: 'Timestamp' },
        { key: 'action', label: 'Action' },
        { key: 'userEmail', label: 'Moderator email' },
        { key: 'submissionId', label: 'Submission ID' },
        { key: 'submissionTitle', label: 'Submission title' },
        { key: 'note', label: 'Note' },
      ],
      filtered.map((e) => ({
        timestamp: e.timestamp,
        action: e.action,
        userEmail: e.userEmail || '',
        submissionId: e.submissionId,
        submissionTitle: e.submissionTitle || '',
        note: e.note || '',
      })),
    );
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`pride-activity-${stamp}.csv`, csv);
  };

  return (
    <div className="activity-log">
      <h1>Activity Log</h1>
      <div className="filter-bar">
        <label className="filter-bar__field">
          <span>Action</span>
          <Select
            options={ACTION_OPTIONS}
            value={actionFilter}
            onValueChange={setActionFilter}
          />
        </label>
        <label className="filter-bar__field">
          <span>User</span>
          <input
            type="text"
            value={userFilter}
            placeholder="email contains…"
            onChange={(e) => setUserFilter(e.target.value)}
            data-testid="activity-user-filter"
            aria-label="User email contains"
          />
        </label>
        {isLeadOrAdmin && (
          <label
            className="filter-bar__field"
            data-testid="activity-moderator-filter"
          >
            <span>Moderator</span>
            <Select
              options={moderatorOptions}
              value={moderatorFilter}
              onValueChange={setModeratorFilter}
            />
          </label>
        )}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="activity-export"
        >
          Export CSV
        </button>
      </div>

      {loading ? (
        // F-008: skeleton rows.
        <table
          className="data-table"
          aria-busy="true"
          data-testid="activity-loading"
        >
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Action</th>
              <th scope="col">User</th>
              <th scope="col">Submission</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cells={5} />
            ))}
          </tbody>
        </table>
      ) : error ? (
        <ErrorState
          message="We could not load the activity log."
          detail={error}
          onRetry={reload}
          testId="activity-error"
        />
      ) : filtered.length === 0 ? (
        // F-108 + F-303: empty state surfaces an actionable copy.
        <EmptyState
          testId="activity-empty"
          title="No activity yet."
          description="As moderators approve, reject, or comment on submissions, the log will fill up here."
        />
      ) : (
        <table className="data-table" data-testid="activity-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Action</th>
              <th scope="col">User</th>
              <th scope="col">Submission</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry, idx) => (
              <tr key={`${entry.submissionId}-${entry.timestamp}-${idx}`}>
                <td>{formatDateTime(entry.timestamp)}</td>
                <td>{entry.action}</td>
                <td>{entry.userEmail}</td>
                <td>
                  <a
                    href={`#/submissions/${entry.submissionId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onSelect(entry.submissionId);
                    }}
                    data-testid={`activity-link-${entry.submissionId}`}
                  >
                    {entry.submissionTitle}
                  </a>
                </td>
                <td>{entry.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
